// Shared constants for the Crude Reports module.
// See api_contract/report/ — general / evaluation / monitoring / facility.
// All four reports share one Advance Search popup and one filter/param shape.
//
// ⚠️ CROSS-HOST: one Advance Search popup spans two backends.
//   - Report list/export, Country lookup, CDU list -> `crudeApi` (rhacrude_api)
//   - Plant (RefineryUnits) lookup                 -> `proHydroApi`    (rha_api)
// Verified via curl 2026-07-14: RefineryUnits/Lookup 404s on rhacrude_api.

// --- rhacrude_api (crudeApi) ---
export const REPORT_ENDPOINTS = {
  GENERAL: {
    LIST: '/v1/Report/GetGeneralReports',
    // SINGULAR, matching the other three reports. The rename landed in dev
    // between 2026-07-15 and 2026-07-20: curl on 2026-07-20 shows the singular
    // routes 401 (exist) and the old plural GetGeneralReports{Excel,Pdf} 404
    // (gone). The plural form is dead — do not restore it.
    EXPORT_EXCEL: '/v1/Report/GetGeneralReportExcel',
    EXPORT_PDF: '/v1/Report/GetGeneralReportPdf',
  },
  EVALUATION: {
    LIST: '/v1/Report/GetEvaluationReports',
    EXPORT_EXCEL: '/v1/Report/GetEvaluationReportExcel',
    EXPORT_PDF: '/v1/Report/GetEvaluationReportPdf',
  },
  MONITORING: {
    LIST: '/v1/Report/GetMonitoringReports',
    EXPORT_EXCEL: '/v1/Report/GetMonitoringReportExcel',
    EXPORT_PDF: '/v1/Report/GetMonitoringReportPdf',
  },
  FACILITY: {
    LIST: '/v1/Report/GetFacilityReports',
    EXPORT_EXCEL: '/v1/Report/GetFacilityReportExcel',
    EXPORT_PDF: '/v1/Report/GetFacilityReportPdf',
  },
};

export const COUNTRY_LOOKUP_ENDPOINT = '/v1/Crude/GetListOriginCountries';
export const CDU_LIST_ENDPOINT = '/v1/CDU/GetListCDU';

// --- rha_api (proHydroApi) ---
// ⚠️ Plant is RefineryUnit, NOT master Fungsi — despite the requirement doc
// saying "master fungsi". Fungsis/Lookup returns a string FungsiCode as its id,
// which cannot satisfy `plantId` (Guid).
export const PLANT_LOOKUP_ENDPOINT = '/v1/RefineryUnits/Lookup';

// --- Static enum dropdowns (serialized as strings by the API) ---
export const ORIGIN_OPTIONS = [
  { value: 'Domestic', label: 'Domestic' },
  { value: 'Import', label: 'Import' },
];

export const SULFUR_CLASSIFICATION_OPTIONS = [
  { value: 'Sweet', label: 'Sweet' },
  { value: 'LightSour', label: 'Light Sour' },
  { value: 'MediumSour', label: 'Medium Sour' },
  { value: 'HeavySour', label: 'Heavy Sour' },
];

export const YIELD_CLASSIFICATION_OPTIONS = [
  { value: 'Light', label: 'Light' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Heavy', label: 'Heavy' },
  { value: 'SuperHeavy', label: 'Super Heavy' },
];

const labelFrom = options => value =>
  options.find(o => o.value === value)?.label ?? value ?? '';

export const sulfurLabel = labelFrom(SULFUR_CLASSIFICATION_OPTIONS);
export const yieldLabel = labelFrom(YIELD_CLASSIFICATION_OPTIONS);

// Monitoring Report shows Origin as the short "DOMP/IMP" form (requirement
// point 1), not the raw enum. The enum value is still what gets sent as a filter.
const ORIGIN_SHORT_LABELS = { Domestic: 'DOMP', Import: 'IMP' };
export const originShortLabel = value =>
  ORIGIN_SHORT_LABELS[value] ?? value ?? '';

// `country` is multi-select; everything else is single.
export const EMPTY_REPORT_FILTERS = {
  plantId: '',
  origin: '',
  country: [],
  sulfurClassification: '',
  yieldClassification: '',
  searchText: '',
};

// Turns the filter state into the query params shared by list + Excel + PDF
// (the export endpoints accept exactly the same filters).
export const buildReportParams = (filters = {}) => {
  const params = {};
  if (filters.plantId) params.plantId = filters.plantId;
  if (filters.origin) params.origin = filters.origin;
  // Multi-select -> comma-separated, Split(',')+Trim() server-side.
  if (filters.country?.length) params.country = filters.country.join(',');
  if (filters.sulfurClassification) {
    params.sulfurClassification = filters.sulfurClassification;
  }
  if (filters.yieldClassification) {
    params.yieldClassification = filters.yieldClassification;
  }
  if (filters.searchText) params.searchText = filters.searchText;
  return params;
};

export const countActiveFilters = (filters = {}) =>
  Object.values(filters).filter(v => (Array.isArray(v) ? v.length : v)).length;

// --- Evaluation Report: per-CDU cell resolution ---
// implementationStatus wins when present; otherwise fall back to technical*.
const IMPLEMENTATION_STATUS_LABELS = {
  1: 'Proven',
  2: 'Not Proven',
  3: 'Not Yet',
};

export const resolveImplementationCell = impl => {
  if (!impl) return { status: '', volPercent: '', mbPerMonth: '' };

  if (
    impl.implementationStatus !== null &&
    impl.implementationStatus !== undefined
  ) {
    return {
      status: IMPLEMENTATION_STATUS_LABELS[impl.implementationStatus] ?? '',
      volPercent: impl.implementationRatio ?? '',
      mbPerMonth: impl.implementationLimit ?? '',
    };
  }

  let status = '';
  if (impl.technicalStatus === true) status = 'Approved';
  else if (impl.technicalStatus === false) status = 'Not Approved';

  return {
    status,
    volPercent: impl.technicalRatio ?? '',
    mbPerMonth: impl.technicalLimit ?? '',
  };
};
